import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileSpreadsheet } from 'lucide-react';
import { useHeroImage } from '@/hooks/useHeroImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIERS = [
  { key: 'basic', label: 'BASIC PRACTICE', description: 'Foundational concepts and practice' },
  { key: 'comps_ready', label: "COMP'S READY", description: '25-question timed tests • Comprehensive coverage • Board exam prep' },
  { key: 'advanced', label: 'ADVANCED', description: 'Advanced practice and mastery' }
];

export default function StudyGuideCenter() {
  const [guidesByTierAndCategory, setGuidesByTierAndCategory] = useState({});
  const [testCategories, setTestCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const heroImage = useHeroImage('study_guide_center');
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [guidesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/study-guides`),
        axios.get(`${API}/test-categories`) // Public endpoint
      ]);
      
      setTestCategories(categoriesRes.data.filter(cat => cat.enabled));
      
      const grouped = {};
      guidesRes.data.forEach(guide => {
        const tier = guide.tier;
        const cat = guide.category;
        if (!grouped[tier]) {
          grouped[tier] = {};
        }
        if (!grouped[tier][cat]) {
          grouped[tier][cat] = [];
        }
        grouped[tier][cat].push(guide);
      });
      
      setGuidesByTierAndCategory(grouped);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (tier, categorySlug) => {
    const guides = guidesByTierAndCategory[tier]?.[categorySlug] || [];
    if (guides.length > 0) {
      navigate(`/study-guide-category/${tier}/${categorySlug}`);
    }
  };

  return (
    <Layout>
      <div 
        className="relative bg-cover bg-center h-[400px] mb-12 flex items-center justify-center"
        style={heroImage ? { backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${heroImage})` } : { background: 'linear-gradient(135deg, #E8934D 0%, #D4722A 100%)' }}
      >
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-7xl retro-title text-[#F4E4C1] mb-4">
            STUDY GUIDES
          </h1>
          <p className="text-xl text-[#F4E4C1] max-w-3xl mx-auto">
            Comprehensive outlines • Organized content • Complete study resources
          </p>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12 space-y-16">
        {loading ? (
          <div className="text-center py-20">
            <div className="text-2xl text-[#6B4423]">Loading study guides...</div>
          </div>
        ) : (
          <>
            {TIERS.map((tier) => {
              const tierCategories = testCategories.filter(cat => cat.tier === tier.key);
              const tierData = guidesByTierAndCategory[tier.key] || {};
              
              if (tierCategories.length === 0) return null;
              
              return (
                <div key={tier.key}>
                  <div className="cassette-card bg-[#F4E4C1] p-8 mb-8 text-center">
                    <h2 className="text-5xl retro-title text-[#2C1810] mb-2">{tier.label}</h2>
                    <p className="text-[#6B4423] text-lg">{tier.description}</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {tierCategories.map((category) => {
                      const count = tierData[category.slug]?.length || 0;
                      const hasContent = count > 0;
                      
                      return (
                        <button
                          key={category.slug}
                          onClick={() => hasContent && handleCategoryClick(tier.key, category.slug)}
                          disabled={!hasContent}
                          className="bg-gradient-to-br from-green-400 via-emerald-400 to-green-500 rounded-xl p-8 text-white transition-all transform hover:scale-105 hover:shadow-2xl disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 relative overflow-hidden"
                          style={{
                            boxShadow: '0 8px 0 rgba(0,0,0,0.2), 0 12px 20px rgba(0,0,0,0.3)',
                            border: '4px solid rgba(255,255,255,0.3)'
                          }}
                        >
                          <div className="relative z-10">
                            <div className="text-6xl mb-4">{category.icon}</div>
                            <h3 className="text-2xl font-black mb-3 tracking-wider drop-shadow-lg">
                              {category.name.toUpperCase()}
                            </h3>
                            <p className="text-sm opacity-90 mb-4">
                              {category.description || 'Study materials'}
                            </p>
                            <div className="flex items-center justify-center gap-2 mt-4">
                              <FileSpreadsheet className="w-5 h-5" />
                              <span className="font-bold">{count} guides</span>
                            </div>
                          </div>
                          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-40"></div>
                          <div className="absolute bottom-0 right-0 w-20 h-20 border-4 border-white opacity-20 rounded-tl-full"></div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {testCategories.length === 0 && (
              <div className="text-center py-20">
                <FileSpreadsheet className="w-24 h-24 text-[#6B4423] mx-auto mb-6 opacity-50" />
                <h3 className="text-3xl retro-title text-[#2C1810] mb-4">
                  No Study Guides Available Yet
                </h3>
                <p className="text-lg text-[#6B4423]">
                  Study guides will appear here once generated from tests in the admin panel.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}